import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { getSystemTheme } from '../../shared/utils/getSystemTheme';
import { getBrowserLanguage } from '../../shared/utils/languages';
import { resolveThemePreference } from '../../shared/utils/resolveThemePreference';
import type { AccentType } from '../../shared/types/AccentType';
import type { LanguageCode } from '../../shared/types/LanguageType';
import type { LayoutType } from '../../shared/types/LayoutType';
import type { ResolvedTheme, ThemePreference } from '../../shared/types/ThemeType';
import type { UiState } from './types';
import type { FolderUiSettings } from './workspaceLogic';
import { migrateLegacyAppStorage } from './migrateLegacyStorage';

migrateLegacyAppStorage();

type UiActions = {
  setLang: (lang: LanguageCode) => void;
  initializeLang: (lang: LanguageCode, hasUser: boolean) => void;
  setIsAuto: (payload: boolean) => void;
  setAccent: (accent: AccentType) => void;
  setMode: (payload: ResolvedTheme) => void;
  setThemePreference: (payload: ThemePreference) => void;
  setLayout: (payload: LayoutType) => void;
  setPreviousLayout: (payload: LayoutType | '') => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;
  applyFolderSettings: (settings: FolderUiSettings) => void;
  resetUi: () => void;
};

const createDefaultUiState = (): UiState => ({
  mode: getSystemTheme(),
  themePreference: 'system',
  themeSchedule: undefined,
  accent: 'brick',
  isAuto: true,
  layout: {
    type: 'sidebar-classic',
    previousType: '',
  },
  sidebarCollapsed: true,
  currentLang: getBrowserLanguage(),
  isInitialized: false,
});

export const useUiStore = create<UiState & UiActions>()(
  persist(
    (set, get) => ({
      ...createDefaultUiState(),

      setLang: (payload) => set({ currentLang: payload }),

      initializeLang: (payload, hasUser) => set({
        currentLang: payload,
        isInitialized: hasUser ? get().isInitialized : true,
      }),

      setIsAuto: (payload) => set({ isAuto: payload }),
      setMode: (payload) => set({ mode: payload }),
      setThemePreference: (payload) => set({ themePreference: payload }),
      setAccent: (payload) => set({ accent: payload }),

      setLayout: (payload) => set((state) => ({
        layout: { ...state.layout, type: payload },
      })),

      setPreviousLayout: (payload) => set((state) => ({
        layout: { ...state.layout, previousType: payload },
      })),

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      toggleSidebarCollapsed: () => set((state) => ({
        sidebarCollapsed: !state.sidebarCollapsed,
      })),

      applyFolderSettings: (settings) => {
        const systemDark = typeof window !== 'undefined'
          ? window.matchMedia('(prefers-color-scheme: dark)').matches
          : false;
        const resolved = resolveThemePreference({
          preference: settings.mode,
          schedule: settings.themeSchedule,
          systemDark,
        });

        set({
          themePreference: settings.mode,
          themeSchedule: settings.mode === 'schedule' ? settings.themeSchedule : undefined,
          mode: resolved,
          isAuto: settings.mode === 'system',
          currentLang: settings.currentLang,
          isInitialized: settings.isInitialized,
          accent: settings.accent,
        });
      },

      resetUi: () => set(createDefaultUiState()),
    }),
    {
      name: 'app-ui',
      version: 1,
      migrate: (persistedState, version) => {
        const state = persistedState as Partial<UiState>;
        if (version === 0 || state.themePreference === undefined) {
          const resolved = state.mode === 'dark' ? 'dark' : 'light';
          return {
            ...createDefaultUiState(),
            ...state,
            mode: resolved,
            themePreference: state.isAuto ? 'system' : resolved,
          } as UiState;
        }
        return persistedState as UiState;
      },
      partialize: (state) => ({
        mode: state.mode,
        themePreference: state.themePreference,
        themeSchedule: state.themeSchedule,
        accent: state.accent,
        isAuto: state.isAuto,
        layout: state.layout,
        sidebarCollapsed: state.sidebarCollapsed,
        currentLang: state.currentLang,
        isInitialized: state.isInitialized,
      }),
    }
  )
);
